import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const PRIMARY = '#2b3380';

function SupplierPortal() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [supplier, setSupplier] = useState(null);
  const [activeTab, setActiveTab] = useState('rfqs');
  const [loading, setLoading] = useState(false);
  const [loginMode, setLoginMode] = useState(true);
  
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [dashboard, setDashboard] = useState(null);
  const [rfqs, setRfqs] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [pos, setPos] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('supplier_token');
    const supplierData = localStorage.getItem('supplier_data');
    if (token && supplierData) {
      setIsLoggedIn(true);
      setSupplier(JSON.parse(supplierData));
      fetchDashboard();
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      if (activeTab === 'rfqs') fetchRfqs();
      else if (activeTab === 'quotations') fetchQuotations();
      else if (activeTab === 'pos') fetchPOs();
    }
  }, [activeTab, isLoggedIn]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/api/supplier/login`, loginForm);
      localStorage.setItem('supplier_token', response.data.access_token);
      localStorage.setItem('supplier_data', JSON.stringify(response.data.supplier));
      setSupplier(response.data.supplier);
      setIsLoggedIn(true);
      fetchDashboard();
    } catch (error) {
      alert(error.response?.data?.detail || 'Login failed');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('supplier_token');
    localStorage.removeItem('supplier_data');
    setIsLoggedIn(false);
    setSupplier(null);
  };

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/api/supplier/dashboard`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('supplier_token')}` }
      });
      setDashboard(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchRfqs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/supplier/rfqs`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('supplier_token')}` }
      });
      setRfqs(response.data.rfqs || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchQuotations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/supplier/quotations`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('supplier_token')}` }
      });
      setQuotations(response.data.quotations || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPOs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/supplier/purchase-orders`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('supplier_token')}` }
      });
      setPos(response.data.purchase_orders || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-8">
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold" style={{ color: PRIMARY }}>Supplier Portal</h1>
            <p className="text-gray-600 mt-2">Zobox - Khushiyon Wala Box</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <h2 className="text-xl font-semibold mb-4">Login</h2>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={loginForm.email}
                onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input
                type="password"
                value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <button
              type="submit"
              className="w-full py-2 text-white rounded-lg font-medium hover:opacity-90"
              style={{ backgroundColor: PRIMARY }}
            >
              Login
            </button>
            <p className="text-center text-sm text-gray-600 mt-4">
              Contact admin for supplier registration
            </p>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: PRIMARY }}>Supplier Portal</h1>
            <p className="text-sm text-gray-600">{supplier?.company_name}</p>
          </div>
          <button
            onClick={handleLogout}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Logout
          </button>
        </div>
      </div>

      {dashboard && (
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-sm text-gray-600">Pending RFQs</p>
              <p className="text-3xl font-bold" style={{ color: PRIMARY }}>{dashboard.pending_rfqs}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-sm text-gray-600">My Quotations</p>
              <p className="text-3xl font-bold" style={{ color: PRIMARY }}>{dashboard.my_quotations}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-sm text-gray-600">Active POs</p>
              <p className="text-3xl font-bold" style={{ color: PRIMARY }}>{dashboard.active_pos}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-sm text-gray-600">Total Business</p>
              <p className="text-2xl font-bold text-green-600">₹{dashboard.total_business_value.toLocaleString()}</p>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6">
        <div className="border-b">
          <div className="flex gap-4">
            {[
              { id: 'rfqs', name: 'RFQs' },
              { id: 'quotations', name: 'My Quotations' },
              { id: 'pos', name: 'Purchase Orders' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-3 font-medium transition-colors ${
                  activeTab === tab.id ? 'border-b-2 text-blue-600' : 'text-gray-600 hover:text-gray-900'
                }`}
                style={activeTab === tab.id ? { borderColor: PRIMARY } : {}}
              >
                {tab.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {loading ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-blue-600"></div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold">
                {activeTab === 'rfqs' ? 'RFQs Sent to Me' : activeTab === 'quotations' ? 'My Submitted Quotations' : 'My Purchase Orders'}
              </h2>
            </div>
            {(activeTab === 'rfqs' ? rfqs : activeTab === 'quotations' ? quotations : pos).length === 0 ? (
              <div className="p-12 text-center">
                <div className="text-6xl mb-4">📋</div>
                <p className="text-gray-600">No data available</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <tbody className="bg-white divide-y divide-gray-200">
                    {(activeTab === 'rfqs' ? rfqs : activeTab === 'quotations' ? quotations : pos).map((item, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <p className="font-medium" style={{ color: PRIMARY }}>
                            {item.rfq_number || item.quotation_number || item.po_number}
                          </p>
                          <p className="text-sm text-gray-600">
                            {activeTab === 'rfqs' && `${item.items?.length || 0} items`}
                            {activeTab === 'quotations' && `₹${item.total_amount?.toLocaleString()}`}
                            {activeTab === 'pos' && `₹${item.total_amount?.toLocaleString()}`}
                          </p>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default SupplierPortal;
